<script setup lang="ts">
/**
 * Sélecteur de devis à rattacher à une demande. Liste les devis du client sur une
 * fenêtre (défaut serveur : 1 mois avant l'ouverture, ajustable via « Depuis le »),
 * avec CA / marge et statut gagné (sale/done) vs en cours.
 */
import { ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { TrophyIcon, ClockIcon, CheckIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import type { AccompQuotePick } from '@/v2/accompagnement/api/accompagnementTypes'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { formatEuros, formatDateShort } from '@/v2/accompagnement/helpers/accompagnementFormat'

const props = defineProps<{ open: boolean; requestId: string }>()
const emit = defineEmits<{ close: [] }>()

const store = useAccompagnementStore()
const toast = useToast()

const dialog = ref<HTMLDialogElement | null>(null)
const picks = ref<AccompQuotePick[]>([])
const loading = ref(false)
const error = ref(false)
const from = ref('')
const attaching = ref<string | null>(null)

async function load() {
  loading.value = true
  error.value = false
  try {
    picks.value = await store.fetchQuotePicks(props.requestId, from.value || undefined)
  } catch {
    error.value = true
    picks.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      from.value = ''
      if (!dialog.value?.open) dialog.value?.showModal()
      void load()
    } else {
      dialog.value?.close()
    }
  },
)

async function attach(pick: AccompQuotePick) {
  attaching.value = pick.quoteUuid
  const res = await store.attachQuote(props.requestId, pick.quoteUuid)
  attaching.value = null
  if (res) {
    pick.alreadyAttached = true
    toast.success('Devis rattaché.')
  } else {
    toast.error(store.actionError ?? 'Échec du rattachement.')
  }
}
</script>

<template>
  <dialog ref="dialog" class="modal" @close="emit('close')">
    <div class="modal-box max-w-2xl">
      <h3 class="text-lg font-semibold">Rattacher un devis</h3>
      <p class="mt-1 text-sm text-base-content/70">
        Devis du client, par défaut depuis un mois avant l'ouverture de la demande.
      </p>

      <div class="mt-3 flex items-end gap-2">
        <div>
          <label for="quote-from" class="mb-1 block text-sm font-medium text-base-content">Depuis le</label>
          <input id="quote-from" v-model="from" type="date" class="input input-bordered" @change="load" />
        </div>
        <button type="button" class="btn btn-ghost btn-sm gap-1" :disabled="loading" @click="load">
          <ArrowPathIcon class="h-4 w-4" :class="loading ? 'animate-spin' : ''" aria-hidden="true" />
          Rafraîchir
        </button>
      </div>

      <div class="mt-3 max-h-96 overflow-y-auto">
        <p v-if="loading && !picks.length" class="py-8 text-center text-base-content/70">Chargement des devis…</p>
        <p v-else-if="error" class="py-8 text-center text-error">Erreur de chargement des devis.</p>
        <p v-else-if="!picks.length" class="py-8 text-center text-base-content/70">
          Aucun devis sur la période pour ce client.
        </p>
        <ul v-else role="list" class="space-y-2">
          <li
            v-for="p in picks"
            :key="p.quoteUuid"
            class="flex items-center justify-between gap-3 rounded-lg border border-base-200 p-3"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-medium tabular-nums text-base-content">{{ p.quoteReference }}</span>
                <span class="badge badge-sm gap-1" :class="p.won ? 'badge-success' : 'badge-ghost'">
                  <component :is="p.won ? TrophyIcon : ClockIcon" class="h-3 w-3" aria-hidden="true" />
                  {{ p.won ? 'Gagné' : 'En cours' }}
                </span>
              </div>
              <p class="mt-0.5 text-sm text-base-content/70">
                <span class="tabular-nums">{{ formatEuros(p.totalCa) }}</span> HT ·
                marge <span class="tabular-nums">{{ formatEuros(p.marge) }}</span>
                <span v-if="p.dateOrder"> · {{ formatDateShort(p.dateOrder) }}</span>
              </p>
            </div>
            <button
              type="button"
              class="btn btn-sm gap-1"
              :class="p.alreadyAttached ? 'btn-ghost' : 'btn-primary'"
              :disabled="p.alreadyAttached || attaching === p.quoteUuid"
              :aria-busy="attaching === p.quoteUuid"
              @click="attach(p)"
            >
              <span v-if="attaching === p.quoteUuid" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
              <CheckIcon v-else-if="p.alreadyAttached" class="h-4 w-4" aria-hidden="true" />
              {{ p.alreadyAttached ? 'Rattaché' : 'Rattacher' }}
            </button>
          </li>
        </ul>
      </div>

      <div class="modal-action">
        <button type="button" class="btn btn-ghost" @click="emit('close')">Fermer</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="emit('close')">Fermer</button>
    </form>
  </dialog>
</template>
